import { useEffect, useMemo } from "react";
import { clStatusOptions, packageCodes, stStatusOptions } from "./dropdown";
import dayjs from "dayjs";

export function generateDatesFromStartOfMonth() {
  const dates = [];
  const today = new Date();

  for (let i = 0; i < 30; i++) {
    const prevDate = new Date(today);
    prevDate.setDate(today.getDate() - i);
    const formattedDate = prevDate.toISOString().split("T")[0];
    dates.push(formattedDate);
  }

  return dates.reverse()
}

export function formatDateToDDMMM(dateString) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' }); // 'May', 'Jun', etc.

  return `${day} ${month}`;
}

export function getActiveMonths(year) {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth(); // 0-based (Jan = 0)

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Default to current year if not provided
  if (!year) year = currentYear;

  let endIndex;

  if (year < currentYear) {
    endIndex = 11; // Full year (Jan to Dec)
  } else if (year === currentYear) {
    endIndex = currentMonthIndex; // Jan to current month
  } else {
    return []; // Future year — no active months
  }

  return months.slice(0, endIndex + 1).map((name, index) => ({
    name,
    year,
    month: index + 1 // 1-based month number
  }));
}

export function getYearsFrom2020() {
  const currentYear = new Date().getFullYear();
  const startYear = 2020;
  const years = [];

  for (let year = startYear; year <= currentYear; year++) {
    years.push(year);
  }

  return years;
}

export const buildCondition = (field, values, isNumeric = false) => {
  if (!values?.length) return "";

  const formattedValues = values
    .map((val) => {
      const cleanVal = String(val).replace(/'/g, "");
      return isNumeric ? cleanVal : `'${cleanVal}'`;
    })
    .join(",");

  return `${field} IN (${formattedValues})`;
};

export const getStudentStatusName = (code) => {
  const res = stStatusOptions.find((item) => item.value === code);
  return res?.label || res?.value;
};
export const getStudentPackageName = (code) => {
  const res = packageCodes.find((item) => item.value === code);
  return res?.label || res?.value;
};
export const getCourseName = (code) => {
  const res = clStatusOptions.find((item) => item.value === code);
  return res?.label || res?.value;
};

export const useMonthlyDateRanges = (range) => {
  const ranges = useMemo(() => {
    const isValidRange =
      Array.isArray(range) &&
      range.length === 2 &&
      range[0] &&
      range[1] &&
      dayjs(range[0], 'YYYY-MM-DD', true).isValid() &&
      dayjs(range[1], 'YYYY-MM-DD', true).isValid();

    if (isValidRange) {
      const startDate = dayjs(range[0]);
      const endDate = dayjs(range[1]);
      const result = [];

      let current = startDate.startOf("month");

      while (current.isBefore(endDate, "day") || current.isSame(endDate, "month")) {
        const start = current.isSame(startDate, "month") ? startDate : current.startOf("month");
        const end = current.isSame(endDate, "month") ? endDate : current.endOf("month");

        result.push({
          start: start.format("YYYY-MM-DD"),
          end: end.format("YYYY-MM-DD"),
        });

        current = current.add(1, "month");
      }

      return result;
    }

    // Fallback: last 12 full months including current partial month
    const result = [];
    const today = dayjs();
    const currentDay = today.date();

    for (let i = 0; i <= 11; i++) {
      const monthStart = today.subtract(i, "month").startOf("month");

      const start = monthStart;
      const end = i === 0
        ? (currentDay === 1 ? start : today)
        : monthStart.endOf("month");

      result.push({
        start: start.format("YYYY-MM-DD"),
        end: end.format("YYYY-MM-DD"),
      });
    }

    return result.reverse(); // Optional: make it chronological
  }, [range?.[0], range?.[1]]); // depend specifically on dates

  return ranges;
};
